import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useConfirm } from 'shared/hooks/useConfirm'
import useRequestPending from 'shared/hooks/useRequestPending'
import { loadOnMount } from 'shared/hoc'
import { sendAmplitudeEvent, useSendGTAGEvent } from 'analytics/utils'
import { ANALYTICS_FLOW_TYPE_SUBSCRIPTION } from 'analytics/constants'
import CircleLoader from 'shared/components/CircleLoader'
import ErrorMessage from 'shared/components/ErrorMessage'
import Pin from 'shared/components/Pin'
import featureToggles from 'shared/featureToggles'

import { Map } from 'booking/components/Map'
import { ParkingInfoSideBar } from 'booking/components/ParkingInfoSideBar'

import { selectVehicle, clearSelectedRate, getBookingLocationDetails, selectSeasonTicket } from 'booking/actions'
import { getSeasonTickets } from 'grs/actions'

import {
    locationDetailsPendingSelector,
    locationDetailsSelector,
    seasonTicketsPendingSelector,
    selectedTicketRatePendingSelector,
    selectedVehicleSelector,
    purchaseCompletedSelector,
} from 'booking/selectors'

import { isDesktop } from 'shared/responsive-helpers'
import { MarkerStyle } from 'shared/generate-marker-style'

import PurchaseFlowHeader from 'subscription-flow/components/PurchaseFlowHeader/PurchaseFlowHeader'
import DateSelection from 'subscription-flow/components/DateSelection'

import SuccessPage from 'subscription-flow/components/SuccessPage'
import VehicleSelection from 'subscription-flow/components/VehicleSelection'

import {
    FLOW_STEP_DATE,
    FLOW_STEP_PRODUCT,
    FLOW_STEP_REVIEW,
    FLOW_STEP_SUCCESS,
    FLOW_STEP_VEHICLE,
    PURCHASE_FLOW_HEADER_PREFIX,
} from 'subscription-flow/constants'

import ProductSelection from './ProductSelection'
import ReviewPay from './ReviewPay'
import DateRate from './DateRate'
import { brazilTicketsSelector } from '../selectors'

import styles from 'subscription-flow/styles.module.scss'

const PURCHASE_FLOW = [FLOW_STEP_PRODUCT, FLOW_STEP_DATE, FLOW_STEP_VEHICLE, FLOW_STEP_REVIEW]

const initialState = {
    product: null,
    startDate: null,
    vehicles: null,
    details: null,
}

const $PurchaseFlow = ({
    location,
    locationPending,
    errorMessage,
    products,
    selectSeasonTicket,
    ticketsPending,
    ratePending,
    selectVehicle,
    purchaseCompleted,
    clearSelectedRate,
    flowSelectedVehicles,
    getSeasonTickets,
}) => {
    const { ConfirmModal } = useConfirm()
    const { locationCode } = useParams()
    const { isLoading: ticketsLoading } = useRequestPending(ticketsPending)
    const { isLoading: locationLoading } = useRequestPending(locationPending)
    const { isLoading: rateLoading } = useRequestPending(ratePending)
    const { grsId } = location
    const [activeStep, setActiveStep] = useState(FLOW_STEP_PRODUCT)
    const [purchaseData, setPurchaseData] = useState(initialState)
    const sendGTAGEvent = useSendGTAGEvent()
    const { geoLocation, name } = location

    useEffect(() => () => clearSelectedRate(), [])

    useEffect(() => {
        if (name) {
            sendAmplitudeEvent('Begin Checkout', {
                'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
                'Location Name': name,
            })

            // TODO review this GTAG event
            sendGTAGEvent('select_location', {
                location_name: name,
            })
        }
    }, [name])

    useEffect(() => {
        if (purchaseCompleted) {
            setSuccess()
        }
    }, [purchaseCompleted])

    const setStartDate = startDate => {
        setPurchaseData({ ...purchaseData, startDate })
        // TODO review this GTAG event
        sendGTAGEvent('select_date', {
            start_date: startDate.value.format('YYYY/MM/DD HH:mm:s'),
        })
        sendAmplitudeEvent('Selected Date', {
            'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
            'Location Name': name,
        })
        setActiveStep(FLOW_STEP_VEHICLE)
        getSeasonTickets(locationCode, startDate)
    }

    const setProduct = product => {
        setPurchaseData({ ...purchaseData, product })
        setActiveStep(FLOW_STEP_DATE)
        sendAmplitudeEvent('Selected Product', {
            'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
            'Location Name': name,
        })

        // TODO review this GTAG event
        sendGTAGEvent('select_product', {
            product_name: product.displayValue,
            product_id: product.rateId,
            price: product.rate.amount,
        })
    }

    const setVehicle = vehicles => {
        selectVehicle(vehicles)
        setPurchaseData({ ...purchaseData, vehicles })
        sendAmplitudeEvent('Vehicle Selected', { 'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION })
        sendGTAGEvent('select_vehicle', { flow_type: ANALYTICS_FLOW_TYPE_SUBSCRIPTION })
        setActiveStep(FLOW_STEP_REVIEW)
    }

    const onStepClick = step => {
        switch (step) {
            case FLOW_STEP_PRODUCT:
                clearSelectedRate()
                setVehicle([])
                setPurchaseData(initialState)
                break
            case FLOW_STEP_DATE:
                setVehicle([])
                break
            default:
                break
        }
        setActiveStep(step)
    }

    const onStartDateChange = date => {
        selectSeasonTicket({ id: purchaseData.product.prdctCd }, locationCode, date)
    }

    const setSuccess = () => {
        setActiveStep(FLOW_STEP_SUCCESS)
    }

    const setDetails = details => {
        setPurchaseData({ ...purchaseData, details: { ...purchaseData.details, ...details } })
    }

    const showContent = !(locationLoading || ticketsLoading) && !errorMessage

    return (
        <div className={styles.purchaseContainer}>
            <ConfirmModal
                title="subscription.purchase.step.confirm.title"
                text="subscription.purchase.step.confirm.text"
            />
            <div className={styles.purchaseScreen}>
                {activeStep === FLOW_STEP_SUCCESS ? (
                    <SuccessPage href="/dashboard/transactions" />
                ) : (
                    <div className={styles.flowContainer}>
                        {(locationLoading || ticketsLoading) && <CircleLoader />}
                        {errorMessage && <ErrorMessage messages={[errorMessage]} />}
                        {showContent && (
                            <div className={styles.flowSection}>
                                <PurchaseFlowHeader
                                    grsId={grsId}
                                    activeStep={activeStep}
                                    stepValues={purchaseData}
                                    locationName={name}
                                    onStepClick={onStepClick}
                                    flowSteps={PURCHASE_FLOW}
                                    stepPrefix={PURCHASE_FLOW_HEADER_PREFIX}
                                />
                                {activeStep === FLOW_STEP_PRODUCT && (
                                    <ProductSelection
                                        onNext={setProduct}
                                        value={purchaseData.product}
                                        products={products}
                                        noQuantity
                                    />
                                )}
                                {activeStep === FLOW_STEP_DATE && (
                                    <DateSelection
                                        isLoading={rateLoading}
                                        onNext={setStartDate}
                                        value={purchaseData.startDate?.value}
                                        onChange={date => onStartDateChange(date)}>
                                        <DateRate setAutoRenew={v => setDetails({ autoRenew: v })} />
                                    </DateSelection>
                                )}
                                {activeStep === FLOW_STEP_VEHICLE && (
                                    <VehicleSelection
                                        onNext={setVehicle}
                                        flowSelectedVehicles={flowSelectedVehicles}
                                        analyticsFlowType={ANALYTICS_FLOW_TYPE_SUBSCRIPTION}
                                    />
                                )}
                                {activeStep === FLOW_STEP_REVIEW && !errorMessage && (
                                    <ReviewPay
                                        purchaseData={purchaseData}
                                        hideCreditCardPayment={
                                            featureToggles.brNeoPurchaseFlow && !location?.dataFields?.allowCard
                                        }
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isDesktop() && showContent && (
                <div className={styles.sidebar}>
                    <ParkingInfoSideBar loading={locationPending} location={location}>
                        {geoLocation && (
                            <Map geoLocation={geoLocation}>
                                <div style={MarkerStyle(30, 30)}>
                                    <Pin name={name} />
                                </div>
                            </Map>
                        )}
                    </ParkingInfoSideBar>
                </div>
            )}
        </div>
    )
}

export const PurchaseFlow = compose(
    connect(
        createStructuredSelector({
            purchaseCompleted: purchaseCompletedSelector,
            locationPending: locationDetailsPendingSelector,
            location: locationDetailsSelector,
            products: brazilTicketsSelector,
            ticketsPending: seasonTicketsPendingSelector,
            ratePending: selectedTicketRatePendingSelector,
            flowSelectedVehicles: selectedVehicleSelector,
        }),
        {
            getBookingLocationDetails,
            getSeasonTickets,
            selectSeasonTicket,
            clearSelectedRate,
            selectVehicle,
        }
    ),
    loadOnMount('getBookingLocationDetails', ({ match }) => [match?.params?.locationCode]),
    loadOnMount('getSeasonTickets', ({ match }) => [match?.params?.locationCode])
)($PurchaseFlow)
